import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { productImage } from '../lib/api';
import StatusBadge from '../components/shared/StatusBadge';
import { Upload, Search, X, Edit2, Trash2, Loader2, AlertTriangle, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { bcIcon } from '../lib/businessCategories';

export default function Products() {
  const { seller } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  // Feb 2026: top-of-page filter is driven by the seller's Business
  // Categories — never expanded into master-category sub-types. `cats` now
  // holds an array of BC name strings, e.g. ["Fabric","Ladies Wear",...].
  const [cats, setCats] = useState([]);
  // Master categories are still needed by the product edit modal (unit,
  // sizes, category-specific fields). Kept in a separate state so the top
  // filter can be pure BC-strings while the modal continues to work.
  const [modalCats, setModalCats] = useState([]);
  const cat = searchParams.get('business_category') || searchParams.get('category') || '';
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [openId, setOpenId] = useState(null);
  // Multi-select + bulk delete (Jun 2026) — same pattern as Catalogue Broadcast
  const [selProd, setSelProd] = useState([]);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const toggleProd = (id) => setSelProd((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const confirmBulkDelete = async () => {
    if (selProd.length === 0) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post('/products/bulk-delete', { ids: selProd });
      toast.success(`${data.deleted} product${data.deleted === 1 ? '' : 's'} deleted successfully.`);
      setSelProd([]);
      setShowBulkConfirm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete products. Please try again.');
    } finally {
      setBulkDeleting(false);
    }
  };

  
const load = () => {
  setLoading(true);

  const params = new URLSearchParams();
  params.set('limit', '100');
  params.set('skip', String((page - 1) * 100));

  if (q) params.set('search', q);

  api.get('/products?' + params.toString())
    .then(({ data }) => {
      setItems(data.items || []);
      setTotalProducts(data.total || 0);
    })
    .catch((e) => {
      toast.error(
        e.response?.data?.detail || 'Failed to load products'
      );
    })
    .finally(() => setLoading(false));
};
  useEffect(() => { setCats(seller?.business_categories || []); }, [seller]);
  useEffect(() => { api.get('/categories').then(({ data }) => setModalCats(data)).catch(() => setModalCats([])); }, []);
  useEffect(() => { load(); }, [q, page]);

  // Client-side filter by Business Category name (matches product.business_category
  // populated on all uploads since Feb 2026).
  const filteredItems = useMemo(() => {
    if (!cat) return items;
    return items.filter(p => (p.business_category || '') === cat);
  }, [items, cat]);

  const activeCat = cat || null;

  const setCat = (val) => {
    const sp = new URLSearchParams(searchParams);
    // Drop legacy `category` param when writing the new one so URLs stay clean.
    sp.delete('category');
    if (val) sp.set('business_category', val); else sp.delete('business_category');
    setSearchParams(sp);
  };

  const openProduct = items.find(p => p.id === openId);

  return (
    <div className="space-y-5 fade-up" data-testid="products-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[140px] sm:min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fab-text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by code or description..." data-testid="search-input" className="fab-input pl-9 w-full px-3 py-2.5 rounded-lg border border-fab-primary-lighter text-[13px] bg-white" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="category-filter" className="fab-input px-3 py-2.5 rounded-lg border border-fab-primary-lighter text-[13px] bg-white flex-1 min-w-[140px] sm:flex-none">
          <option value="">All Categories</option>
          {cats.map((c) => <option key={c} value={c}>{bcIcon(c)} {c}</option>)}
        </select>
        <Link to="/dashboard/products/upload" data-testid="bulk-upload-link" className="btn-primary text-[13px] flex items-center justify-center gap-2 w-full sm:w-auto"><Upload className="w-4 h-4" /> Add Products</Link>
      </div>

      {selProd.length > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap rounded-lg bg-red-50 border border-red-200 px-3 py-2" data-testid="product-selection-bar">
          <span className="text-[13px] font-semibold text-red-800" data-testid="product-selected-count">
            {selProd.length} product{selProd.length === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelProd([])} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-fab-text-secondary hover:bg-white" data-testid="product-clear-selection">Clear</button>
            <button
              onClick={() => setShowBulkConfirm(true)}
              data-testid="delete-selected-products"
              className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete Selected
            </button>
          </div>
        </div>
      )}

      {activeCat && (
        <div className="text-[12px] text-fab-text-secondary" data-testid="active-category-label">
          Showing <strong className="text-fab-text">{filteredItems.length}</strong> product{filteredItems.length === 1 ? '' : 's'} in <strong className="text-fab-primary">{bcIcon(activeCat)} {activeCat}</strong>
          {' · '}
          <button className="text-fab-primary font-semibold underline" onClick={() => setCat('')}>Clear filter</button>
        </div>
      )}

      {loading ? <div className="text-fab-text-muted">Loading...</div> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="products-grid">
          {filteredItems.length === 0 && <div className="col-span-full text-center text-fab-text-muted py-12">No products yet. Click &ldquo;Add Products&rdquo; to create your first listing in 30 seconds.</div>}
          {filteredItems.map((p) => (
            <div
              key={p.id}
              data-testid={`product-card-${p.product_code}`}
              className={`fab-card overflow-hidden group relative border-2 transition-all ${selProd.includes(p.id) ? 'border-fab-primary ring-2 ring-fab-primary' : 'border-transparent hover:shadow-md hover:border-fab-primary'}`}
            >
              <label
                className="absolute top-2 left-2 z-10 w-6 h-6 rounded-md bg-white/90 border border-fab-primary-lighter flex items-center justify-center cursor-pointer shadow-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selProd.includes(p.id)}
                  onChange={() => toggleProd(p.id)}
                  className="accent-fab-primary w-4 h-4 cursor-pointer"
                  data-testid={`product-select-${p.product_code}`}
                />
              </label>
              <button
                type="button"
                onClick={() => setOpenId(p.id)}
                data-testid={`product-open-${p.product_code}`}
                className="block w-full text-left cursor-pointer"
              >
                <div className="relative aspect-square bg-fab-elevated">
                  <img src={productImage(p, 'medium')} alt={p.product_code} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-black/65 text-white text-[10px] font-mono font-bold">{p.product_code}</div>
                  <div className="absolute top-2 right-2"><StatusBadge status={p.status} size="sm" /></div>
                </div>
                <div className="p-3 space-y-1">
                  <div className="text-[11px] text-fab-text-muted">{p.category_name}</div>
                  <div className="text-[13px] font-semibold text-fab-text truncate">{p.description || p.name}</div>
                  <div className="flex items-center justify-between pt-1">
                    <div className="font-display font-bold text-fab-text">₹{p.price}</div>
                    <div className="text-[11px] text-fab-text-muted">MOQ {p.moq}</div>
                  </div>
                  {(() => {
                    const available = p.stock_type === 'THAAN'
  ? Math.max(
      0,
      (p.thaans || [])
        .filter(t => (t.status || 'AVAILABLE') !== 'DEPLETED')
        .reduce(
          (sum, t) =>
            sum +
            (Number(t.thaan_length) || 0) *
              (Number(t.number_of_thaans) || 0),
          0
        )
    )
  : Math.max(
      0,
      Number(p.stock || 0) -
        Number(p.reserved || 0) -
        Number(p.sold || 0)
    );

                    return (
                      <div className={`text-[11px] font-semibold ${available < 10 ? 'text-fab-warning' : 'text-fab-wa-dark'}`}>
                        {Number(available).toFixed(2).replace(/\.?0+$/, '')} available
                      </div>
                    );
                  })()}
                </div>
              </button>
            </div>
          ))}
        </div>
      )}

      {totalProducts > 100 && (
        <div className="flex items-center justify-center gap-4 py-6">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-4 py-2 rounded-lg border disabled:opacity-40"
          >
            Previous
          </button>

          <span className="text-sm">
            Page {page} of {Math.ceil(totalProducts / 100)}
          </span>

          <button
            type="button"
            disabled={page >= Math.ceil(totalProducts / 100)}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 rounded-lg border disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
      {openProduct && (
        <ProductDetailModal
          product={openProduct}
          categories={modalCats}
          onClose={() => setOpenId(null)}
          onSaved={() => { setOpenId(null); load(); }}
          onDeleted={() => { setOpenId(null); load(); }}
        />
      )}

      {showBulkConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !bulkDeleting && setShowBulkConfirm(false)}
          data-testid="bulk-delete-confirm"
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-fab-text text-[15px]" data-testid="bulk-delete-title">
                  Delete {selProd.length} selected product{selProd.length === 1 ? '' : 's'}?
                </div>
                <div className="text-[13px] text-fab-text-secondary mt-1">
                  These products will be permanently removed from your product catalogue. This action cannot be undone.
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setShowBulkConfirm(false)}
                disabled={bulkDeleting}
                data-testid="bulk-delete-cancel"
                className="px-4 py-2 rounded-lg border border-fab-primary-lighter text-fab-text-secondary text-[13px] font-semibold hover:bg-fab-elevated disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                data-testid="bulk-delete-confirm-btn"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-[13px] font-semibold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                {bulkDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductDetailModal({ product, categories, onClose, onSaved, onDeleted }) {
  const [editing, setEditing] = useState(false);
  
const displayStock = product.stock_type === 'THAAN'
  ? Math.max(0, Number(product.stock_meters ?? product.stock) || 0)
  : Math.max(0, (Number(product.stock) || 0) - (Number(product.reserved) || 0) - (Number(product.sold) || 0));

    const [form, setForm] = useState(() => ({
    name: product.name || '',
    description: product.description || '',
    price: product.price ?? '',
    moq: product.moq ?? 1,
    stock: product.stock ?? 0,
    category_id: product.category_id || '',
    fabric_type: product.fabric_type || '',
    colors: (product.colors || []).join(', '),
    sizes: product.sizes || [],
    saree_length_m: product.saree_length_m ?? '',
    blouse_piece_included: product.blouse_piece_included ?? null,
    material_details: product.material_details || '',
    dupatta_length_m: product.dupatta_length_m ?? '',
    home_furnishing_type: product.home_furnishing_type || '',

    // Additional saved product specifications
    width: product.width ?? '',
    gsm: product.gsm ?? '',
    construction: product.construction || '',
    printing_technique: product.printing_technique || '',
    wash_care: product.wash_care || '',
    inferred_product_type: product.inferred_product_type || '',
    business_category: product.business_category || '',
    gst_status: product.gst_status || '',
    shipping_policy: product.shipping_policy || '',
    quantity_unit: product.quantity_unit || '',
    stock_type: product.stock_type || 'REGULAR',
    stock_meters: product.stock_meters ?? '',
    thaans: product.thaans || [],
    size_stock: product.size_stock || [],
  }));
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get('/categories/_master').then(({ data }) => setMeta(data));
  }, []);

  const cat = categories.find(c => c.id === form.category_id);
  const slug = cat?.slug;
  const masterEntry = meta?.items?.find(m => m.code_prefix === cat?.code_prefix);
  const sizeOptions = masterEntry?.size_options || [];
  const isFabric = slug === 'fabric';
  const isSaree = slug === 'saree';
  const isDressMaterial = slug === 'dress_material';
  const isDupatta = slug === 'dupatta';
  const isHomeFurnishing = slug === 'home_furnishing';

  const toggleSize = (sz) => {
    setForm(f => ({ ...f, sizes: f.sizes.includes(sz) ? f.sizes.filter(s => s !== sz) : [...f.sizes, sz] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        width: form.width === '' ? null : Number(form.width),
        gsm: form.gsm === '' ? null : Number(form.gsm),
        stock_meters: form.stock_meters === '' ? null : Number(form.stock_meters),
        thaans: Array.isArray(form.thaans) ? form.thaans : [],
        size_stock: Array.isArray(form.size_stock) ? form.size_stock : [],
        colors: form.colors.split(',').map(s => s.trim()).filter(Boolean),
        price: form.price === '' ? null : Number(form.price),
        moq: form.moq === '' ? null : Number(form.moq),
        stock: form.stock === '' ? null : Number(form.stock),
        saree_length_m: form.saree_length_m === '' ? null : Number(form.saree_length_m),
        dupatta_length_m: form.dupatta_length_m === '' ? null : Number(form.dupatta_length_m),
      };
      await api.put(`/products/${product.id}`, payload);
      toast.success('Product updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/products/${product.id}`);
      toast.success('Product deleted');
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" data-testid="product-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-4 sm:px-5 py-3 border-b border-fab-primary-lighter flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] text-fab-text-muted font-mono uppercase tracking-wider">{product.product_code}</div>
            <div className="font-display font-bold text-fab-text text-[15px] truncate">{form.name || product.name}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!editing && !confirming && (
              <>
                <button data-testid="product-edit-btn" onClick={() => setEditing(true)} className="px-3 py-1.5 rounded-lg border border-fab-primary-lighter text-[12px] font-semibold text-fab-text-secondary hover:bg-fab-elevated flex items-center gap-1.5">
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
                <button data-testid="product-delete-btn" onClick={() => setConfirming(true)} className="px-3 py-1.5 rounded-lg border border-red-200 text-[12px] font-semibold text-red-600 hover:bg-red-50 flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </>
            )}
            <button data-testid="product-close" onClick={onClose} className="p-1.5 hover:bg-fab-elevated rounded-lg"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 grid grid-cols-1 md:grid-cols-5 gap-4 sm:gap-5">
          <div className="md:col-span-2 space-y-3">
            <img src={productImage(product, 'full')} alt={product.product_code} className="w-full rounded-xl object-cover aspect-square" />
            {(product.images || []).slice(1).length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {(product.images || []).slice(1).map((img, i) => (
                  <img key={i} src={img.paths?.thumb_url || img.paths?.thumb} alt="" className="aspect-square rounded-md object-cover" />
                ))}
              </div>
            )}
            <div className="text-[11px] text-fab-text-muted">
              Created {product.created_at ? new Date(product.created_at).toLocaleDateString() : '—'} · Updated {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : '—'}
            </div>
          </div>

          <div className="md:col-span-3 space-y-3">
            {confirming ? (
              <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 space-y-3" data-testid="delete-confirm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                  <div>
                    <div className="font-semibold text-red-800">Delete this product?</div>
                    <div className="text-[12px] text-red-700">
                      <code className="font-mono">{product.product_code}</code> will be permanently removed. Customers with this in their cart will see it go out of stock. This cannot be undone.
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setConfirming(false)} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-fab-text-secondary hover:bg-white">Keep product</button>
                  <button data-testid="product-delete-confirm" onClick={remove} disabled={saving} className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-50">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    Yes, delete
                  </button>
                </div>
              </div>
            ) : (
              <>
                <Row label="Category">
                  {editing ? (
                    <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value, sizes: [] })} data-testid="product-edit-category" className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]">
                      {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                    </select>
                  ) : <span className="text-[13px] text-fab-text">{cat?.icon} {cat?.name}</span>}
                </Row>

                <Row label="Product name">
                  {editing ? (
                    <input data-testid="product-edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" />
                  ) : <span className="text-[13px] text-fab-text">{product.name}</span>}
                </Row>

                <Row label="Description (AI catalog blurb)">
                  {editing ? (
                    <textarea data-testid="product-edit-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" />
                  ) : <span className="text-[13px] text-fab-text-secondary whitespace-pre-wrap">{product.description || <em>(no description)</em>}</span>}
                </Row>

                <div className="grid grid-cols-3 gap-3">
                  <Row label={`Price (₹${isFabric ? '/m' : ''})`}>
                    {editing ? <input data-testid="product-edit-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" /> : <span className="text-[13px] text-fab-text">₹{product.price}</span>}
                  </Row>
                  <Row label={`MOQ (${isFabric ? 'm' : 'pcs'})`}>
                    {editing ? <input data-testid="product-edit-moq" type="number" min="1" value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" /> : <span className="text-[13px] text-fab-text">{product.moq}</span>}
                  </Row>
                  <Row label={`Stock (${isFabric ? 'm' : 'pcs'})`}>
                    {editing ? <input data-testid="product-edit-stock" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" /> : <span className="text-[13px] text-fab-text">{Number(displayStock).toLocaleString('en-IN', { maximumFractionDigits: 2 })} available</span>}
                  </Row>
                </div>

                <Row label="Fabric type">
                  {editing ? <input value={form.fabric_type} onChange={(e) => setForm({ ...form, fabric_type: e.target.value })} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" /> : <span className="text-[13px] text-fab-text">{product.fabric_type || '—'}</span>}
                </Row>

                <Row label="Available colors (comma-separated)">
                  {editing ? <input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="Red, Blue, Mustard" className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" /> : (
                    <span className="text-[13px] text-fab-text-secondary">{(product.colors || []).join(', ') || '—'}</span>
                  )}
                </Row>
                {/* Additional saved product specifications */}
<Row label="Product type">
  {editing ? (
    <input
      value={form.inferred_product_type}
      onChange={(e) => setForm({ ...form, inferred_product_type: e.target.value })}
      className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
    />
  ) : (
    <span className="text-[13px] text-fab-text">{product.inferred_product_type || '-'}</span>
  )}
</Row>

<Row label="Width">
  {editing ? (
    <input
      type="number"
      value={form.width}
      onChange={(e) => setForm({ ...form, width: e.target.value })}
      className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
    />
  ) : (
    <span className="text-[13px] text-fab-text">{product.width ?? '-'}</span>
  )}
</Row>

<Row label="GSM">
  {editing ? (
    <input
      type="number"
      value={form.gsm}
      onChange={(e) => setForm({ ...form, gsm: e.target.value })}
      className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
    />
  ) : (
    <span className="text-[13px] text-fab-text">{product.gsm ?? '-'}</span>
  )}
</Row>

<Row label="Construction">
  {editing ? (
    <input
      value={form.construction}
      onChange={(e) => setForm({ ...form, construction: e.target.value })}
      className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
    />
  ) : (
    <span className="text-[13px] text-fab-text">{product.construction || '-'}</span>
  )}
</Row>

<Row label="Printing technique">
  {editing ? (
    <input
      value={form.printing_technique}
      onChange={(e) => setForm({ ...form, printing_technique: e.target.value })}
      className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
    />
  ) : (
    <span className="text-[13px] text-fab-text">{product.printing_technique || '-'}</span>
  )}
</Row>

<Row label="Wash care">
  {editing ? (
    <input
      value={form.wash_care}
      onChange={(e) => setForm({ ...form, wash_care: e.target.value })}
      className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
    />
  ) : (
    <span className="text-[13px] text-fab-text">{product.wash_care || '-'}</span>
  )}
</Row>

<Row label="Quantity unit">
  {editing ? (
    <input
      value={form.quantity_unit}
      onChange={(e) => setForm({ ...form, quantity_unit: e.target.value })}
      className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
    />
  ) : (
    <span className="text-[13px] text-fab-text">{product.quantity_unit || '-'}</span>
  )}
</Row>
                {sizeOptions.length > 0 && (
                  <Row label={`${masterEntry.name} sizes`}>
                    {editing ? (
                      <div className="flex flex-wrap gap-1.5" data-testid="product-edit-sizes">
                        {sizeOptions.map(sz => (
                          <button type="button" key={sz} onClick={() => toggleSize(sz)} className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${form.sizes.includes(sz) ? 'bg-fab-primary text-white' : 'bg-fab-elevated text-fab-text-secondary'}`}>
                            {sz}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[13px] text-fab-text">{(product.sizes || []).join(', ') || '—'}</span>
                    )}
                  </Row>
                )}

                {isSaree && (
                  <div className="grid grid-cols-2 gap-3">
                    <Row label="Saree length (m, optional)">
                      {editing ? <input type="number" step="0.1" value={form.saree_length_m} onChange={(e) => setForm({ ...form, saree_length_m: e.target.value })} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" /> : <span className="text-[13px] text-fab-text">{product.saree_length_m ?? '—'}</span>}
                    </Row>
                    <Row label="Blouse piece included">
                      {editing ? (
                        <select value={form.blouse_piece_included ?? ''} onChange={(e) => setForm({ ...form, blouse_piece_included: e.target.value === '' ? null : e.target.value === 'true' })} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]">
                          <option value="">—</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : <span className="text-[13px] text-fab-text">{product.blouse_piece_included == null ? '—' : product.blouse_piece_included ? 'Yes' : 'No'}</span>}
                    </Row>
                  </div>
                )}

                {isDressMaterial && (
                  <Row label="Material details (optional)">
                    {editing ? <textarea rows={2} value={form.material_details} onChange={(e) => setForm({ ...form, material_details: e.target.value })} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" /> : <span className="text-[13px] text-fab-text-secondary whitespace-pre-wrap">{product.material_details || '—'}</span>}
                  </Row>
                )}

                {isDupatta && (
                  <Row label="Length (m, optional)">
                    {editing ? <input type="number" step="0.1" value={form.dupatta_length_m} onChange={(e) => setForm({ ...form, dupatta_length_m: e.target.value })} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" /> : <span className="text-[13px] text-fab-text">{product.dupatta_length_m ?? '—'}</span>}
                  </Row>
                )}

                {isHomeFurnishing && (
                  <Row label="Product type">
                    {editing ? (
                      <select value={form.home_furnishing_type} onChange={(e) => setForm({ ...form, home_furnishing_type: e.target.value })} className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]">
                        <option value="">—</option>
                        {(meta?.home_furnishing_types || []).map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    ) : <span className="text-[13px] text-fab-text">{product.home_furnishing_type || '—'}</span>}
                  </Row>
                )}
              </>
            )}
          </div>
        </div>

        {editing && !confirming && (
          <div className="px-5 py-3 border-t border-fab-primary-lighter flex items-center justify-end gap-2">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-fab-text-secondary hover:bg-fab-elevated">Cancel</button>
            <button data-testid="product-save-btn" onClick={save} disabled={saving} className="btn-primary px-4 py-1.5 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="text-[10px] text-fab-text-muted uppercase tracking-wider font-semibold mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}
